<script setup lang="ts">
import UserAvatar from "@/components/shared/UserAvatar.vue";
import ChatDockTabBase from "@/components/models/message/chatDock/tabs/ChatDockTabBase.vue";
import { useRoomsInfiniteQuery } from "@/composables/models/message/useRoom.ts";
import { ERoomType, type TChatRoom } from "@/api/models/message/room";
import { useAuthStore } from "@/stores/authStore.ts";
import { formatName } from "@/utils/Formatters.ts";
import { formatDate } from "@/utils/Formatters";
import type { ComputedRef } from "vue";

defineProps<{
  active: boolean;
  collapsed: boolean;
  search: string;
  selectedId: string;
}>();

const emit = defineEmits<{
  (e: "select", payload: any): void;
  (e: "update:loading", value: boolean): void;
}>();

const authStore = useAuthStore();

const useInvitesList = (params: { filters: ComputedRef<any>; enabled: ComputedRef<boolean> }) =>
  useRoomsInfiniteQuery(params.filters, params.enabled);

const findInvitedStaff = (room: TChatRoom) => {
  const loggedStaffId = authStore.loggedAsStaff?.id;
  return (room.members ?? []).find((member) => member.staff !== loggedStaffId);
};

const displayName = (room: TChatRoom) => {
  const other = findInvitedStaff(room);
  return other ? formatName(other) : "";
};
</script>

<template>
  <chat-dock-tab-base
    kind="invites"
    :active="active"
    :collapsed="collapsed"
    :search="search"
    :use-list="useInvitesList"
    :build-filters="
      (s) => ({
        search: s,
        page_size: 100,
        room_type__in: [ERoomType.INVITE],
      })
    "
    :select-items="(data) => data?.pages?.flatMap((p: any) => p.results) ?? []"
    :select-id="(p: any) => p.id"
    @select="emit('select', $event)"
    @update:loading="emit('update:loading', $event)"
  >
    <template #collapsed="{ item, select }">
      <v-btn
        v-tippy="{
          content: displayName(item),
          placement: 'right',
        }"
        icon
        variant="text"
        class="tw:!p-1 tw:!min-w-10 tw:!h-10 tw:!w-10 tw:!rounded-full tw:!overflow-visible"
        :class="item.id === selectedId ? 'tw:ring-3 tw:ring-secondary' : ''"
        @click="select"
      >
        <user-avatar
          :staff-id="findInvitedStaff(item)?.staff"
          size="40"
          rounded="circle"
          hide-badges
        />
      </v-btn>
    </template>

    <template #expanded="{ item, select }">
      <v-list-item
        variant="text"
        class="tw:!my-0.5 tw:!rounded-xl tw:!bg-transparent tw:!text-current hover:tw:!bg-current/10"
        rounded="md"
        active-class="tw:!bg-current/15 tw:!ring-2 tw:!ring-inset tw:!ring-current/30"
        @click="select"
        :active="item.id === selectedId"
      >
        <template #prepend>
          <user-avatar
            :staff-id="findInvitedStaff(item)?.staff"
            size="40"
            rounded="circle"
            class="tw:!mr-2"
            hide-badges
          />
        </template>

        <template #title>
          <span class="tw:text-ms tw:font-bold">{{ displayName(item) }}</span>
        </template>

        <template #subtitle>
          <span class="tw:text-xxs tw:font-semibold tw:text-current/70">
            {{ formatDate(item.created_at) }}
          </span>
        </template>
      </v-list-item>
    </template>
  </chat-dock-tab-base>
</template>
